import React, { useEffect, useMemo, useState } from 'react';
import {
  TELEPORT_EXTENSION_POLICY,
  TeleportExtensionPolicyConfig,
  TeleportExtensionPolicyValue,
  getTeleportExtensionPolicy,
  setTeleportExtensionPolicy,
  teleportExtensionPolicyLabel,
} from '../services/teleportExtensionPolicyService';

/**
 * TeleportExtensionPolicySelector
 *
 * Country admin UI for the per-country **free teleport-extension policy**
 * for AI-agent live broadcasts.
 *
 * Tri-state policy (matches the AgentCountryPolicy + WebRTC selectors):
 *
 *   - Not set    — no admin decision; resolves to the env-driven global
 *                  default (currently Allowed). Behaviour identical to
 *                  Allowed when the env-default is unchanged.
 *   - Allowed    — server may auto-extend the AI-agent broadcast for
 *                  free in `chunkSeconds` increments capped at
 *                  `maxTotalAutoExtensionSeconds` per session, while at
 *                  least one viewer is attached to a teleport HUD.
 *   - Disallowed — server may NOT auto-extend; broadcast cuts at
 *                  `scheduledEndAt` like a normal paid session.
 *
 * Numeric tunables are rendered as paired inputs but clamped server-
 * side by the mirror's `_normalise` so a typo can't stuff "extend by
 * 1 second" or "no cap" into the system.
 *
 * IMPORTANT — UX hint: the save button writes to the backend mirror
 * (no on-chain transaction), so Freighter / the passkey prompt does
 * NOT open.
 */

export interface TeleportExtensionPolicySelectorProps {
  country: number;
  canEdit: boolean;
  walletAddress?: string;
  onChange?: (config: TeleportExtensionPolicyConfig) => void;
}

const ALL_VALUES: TeleportExtensionPolicyValue[] = [
  TELEPORT_EXTENSION_POLICY.NOT_SET,
  TELEPORT_EXTENSION_POLICY.ALLOWED,
  TELEPORT_EXTENSION_POLICY.DISALLOWED,
];

const TeleportExtensionPolicySelector: React.FC<TeleportExtensionPolicySelectorProps> = ({
  country, canEdit, walletAddress, onChange,
}) => {
  const [config, setConfig] = useState<TeleportExtensionPolicyConfig | null>(null);
  const [policyValue, setPolicyValue] = useState<TeleportExtensionPolicyValue>(TELEPORT_EXTENSION_POLICY.NOT_SET);
  const [chunkSeconds, setChunkSeconds] = useState<number>(60);
  const [maxTotalSec,  setMaxTotalSec]  = useState<number>(1800);
  const [reasonInput,  setReasonInput]  = useState<string>('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let active = true;
    getTeleportExtensionPolicy(country).then((c) => {
      if (!active) return;
      setConfig(c);
      setPolicyValue(c.policy);
      setChunkSeconds(c.chunkSeconds);
      setMaxTotalSec(c.maxTotalAutoExtensionSeconds);
      setReasonInput(c.raw?.reason || '');
      setDirty(false);
    }).catch(() => { if (active) setError('load_failed'); });
    return () => { active = false; };
  }, [country]);

  const reasonError = useMemo(() => {
    if (reasonInput.length > 240) return 'Reason must be <= 240 characters.';
    return null;
  }, [reasonInput]);
  const numericError = useMemo(() => {
    if (!Number.isFinite(chunkSeconds) || chunkSeconds < 10 || chunkSeconds > 600) {
      return 'chunkSeconds must be between 10 and 600.';
    }
    if (!Number.isFinite(maxTotalSec) || maxTotalSec < chunkSeconds || maxTotalSec > 86400) {
      return 'maxTotalAutoExtensionSeconds must be between chunkSeconds and 86400.';
    }
    return null;
  }, [chunkSeconds, maxTotalSec]);

  async function save() {
    if (!canEdit) return;
    if (reasonError || numericError) {
      setError(reasonError || numericError);
      return;
    }
    setBusy(true); setError(null); setSavedAt(null);
    try {
      const next = await setTeleportExtensionPolicy(country, {
        walletAddress,
        policy:                       policyValue,
        chunkSeconds,
        maxTotalAutoExtensionSeconds: maxTotalSec,
        reason:                       reasonInput.trim() || null,
        policyVersion:                ((config?.raw?.policyVersion) || 0) + 1,
      });
      setConfig(next);
      setPolicyValue(next.policy);
      setChunkSeconds(next.chunkSeconds);
      setMaxTotalSec(next.maxTotalAutoExtensionSeconds);
      setReasonInput(next.raw?.reason || '');
      setDirty(false);
      setSavedAt(Date.now());
      onChange?.(next);
    } catch (err: any) {
      setError(err?.message || 'save_failed');
    } finally {
      setBusy(false);
    }
  }

  function revert() {
    if (!config) return;
    setPolicyValue(config.policy);
    setChunkSeconds(config.chunkSeconds);
    setMaxTotalSec(config.maxTotalAutoExtensionSeconds);
    setReasonInput(config.raw?.reason || '');
    setDirty(false);
    setError(null);
  }

  return (
    <div style={{ padding: '10px 12px', border: '1px solid #2a3a4a', borderRadius: 8, background: '#0d1721' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
        Free teleport extension policy
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, marginBottom: 10 }}>
        Controls whether the server may auto-extend AI-agent live broadcasts
        for free while a viewer is attached to a teleport HUD. Extensions
        happen in <em>chunkSeconds</em> increments capped at
        <em> maxTotalAutoExtensionSeconds</em> per session.
        {' '}<em>Not set</em> resolves to the env-driven global default.
      </div>

      {/* --- Tri-state policy --- */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {ALL_VALUES.map((v) => {
          const active = v === policyValue;
          return (
            <button
              key={v}
              disabled={!canEdit || busy}
              onClick={() => { setPolicyValue(v); setDirty(true); }}
              style={{
                padding: '6px 10px', borderRadius: 6, fontSize: 12,
                cursor: canEdit ? 'pointer' : 'default',
                background: active
                  ? (v === TELEPORT_EXTENSION_POLICY.DISALLOWED ? '#b91c1c' : '#1d4ed8')
                  : '#111c26',
                color:      active ? '#fff'    : '#cbd5e1',
                border: '1px solid ' + (active
                  ? (v === TELEPORT_EXTENSION_POLICY.DISALLOWED ? '#b91c1c' : '#1d4ed8')
                  : '#2a3a4a'),
                opacity: canEdit ? 1 : 0.7,
              }}
            >{teleportExtensionPolicyLabel(v)}</button>
          );
        })}
      </div>

      {/* --- Numeric tunables --- */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
        <div>
          <label style={labelStyle}>chunkSeconds (10-600)</label>
          <input
            type="number"
            min={10}
            max={600}
            step={5}
            value={chunkSeconds}
            disabled={!canEdit || busy}
            onChange={(e) => { setChunkSeconds(Number(e.target.value)); setDirty(true); }}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>maxTotalAutoExtensionSeconds (chunk-86400)</label>
          <input
            type="number"
            min={chunkSeconds}
            max={86400}
            step={60}
            value={maxTotalSec}
            disabled={!canEdit || busy}
            onChange={(e) => { setMaxTotalSec(Number(e.target.value)); setDirty(true); }}
            style={inputStyle}
          />
        </div>
      </div>

      {/* --- Reason (optional, free text) --- */}
      <div style={{ marginTop: 12 }}>
        <label style={labelStyle}>
          Reason <span style={{ color: '#64748b' }}>(optional)</span>
        </label>
        <input
          type="text"
          value={reasonInput}
          maxLength={240}
          disabled={!canEdit || busy}
          onChange={(e) => { setReasonInput(e.target.value); setDirty(true); }}
          placeholder='e.g. "Agent broadcasts are part of the immersive tourism pilot — keep extensions on."'
          style={inputStyle}
        />
      </div>

      {/* --- Currently effective --- */}
      {config && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>
          Currently:
          <strong style={{ color: '#cbd5e1', marginLeft: 4 }}>
            {teleportExtensionPolicyLabel(config.policy)}
          </strong>
          {' '}· chunk {config.chunkSeconds}s · cap {config.maxTotalAutoExtensionSeconds}s
          {config.usingDefault
            ? <span style={{ color: '#facc15' }}> · using default (env-driven)</span>
            : config.raw?.policyVersion
              ? <span> · v{config.raw.policyVersion}</span>
              : null}
          {config.raw?.walletAddress && (
            <span> · last set by <code style={{ color: '#cbd5e1' }}>
              {config.raw.walletAddress.slice(0, 6)}…{config.raw.walletAddress.slice(-4)}
            </code></span>
          )}
        </div>
      )}

      {/* --- Actions --- */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <button
            onClick={save}
            disabled={busy || !dirty || !!reasonError || !!numericError}
            style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: (busy || !dirty || !!reasonError || !!numericError) ? '#1e293b' : '#1d4ed8',
              color:      (busy || !dirty || !!reasonError || !!numericError) ? '#64748b' : '#fff',
              border: 'none', cursor: (busy || !dirty || !!reasonError || !!numericError) ? 'not-allowed' : 'pointer',
            }}
            title={reasonError || numericError || undefined}
          >{busy ? 'Saving…' : 'Save extension policy'}</button>
          {dirty && (
            <button
              onClick={revert}
              disabled={busy}
              style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 12,
                background: '#111c26', color: '#cbd5e1',
                border: '1px solid #2a3a4a', cursor: 'pointer',
              }}
            >Revert</button>
          )}
          {savedAt && !dirty && !error && (
            <span style={{ fontSize: 11, color: '#34d399' }}>Saved.</span>
          )}
        </div>
      )}

      {/* Mirror-only write hint — same idiom as AgentCountryPolicySelector. */}
      {canEdit && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
          This writes to the backend policy mirror — no on-chain
          transaction is required, so your wallet will <em>not</em>
          open Freighter / the passkey prompt. Authorisation is by
          country/global admin role attached to <code>{walletAddress
            ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}`
            : 'the connected wallet'}</code>.
        </div>
      )}

      {(error || reasonError || numericError) && (
        <div style={{ color: '#f87171', fontSize: 12, marginTop: 6, wordBreak: 'break-word' }}>
          {error || reasonError || numericError}
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '5px 7px',
  fontSize: 12,
  backgroundColor: '#111c26',
  border: '1px solid #2a3a4a',
  borderRadius: 4,
  color: '#e2e8f0',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: '#94a3b8',
  marginBottom: 3,
};

export default TeleportExtensionPolicySelector;
